import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {push} from 'connected-react-router';
import {FaBookOpen, FaCalendarAlt, FaCheckCircle, FaClock, FaDownload, FaExclamationTriangle, FaMicrophone, FaPlayCircle, FaRobot, FaSearch, FaTv, FaUsers, FaWallet} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import {
  filterBookableFields,
  filterBookingFieldId,
  getPartnerFields,
  getPartnerServices,
  getSlots,
  partnerSelected,
  rtvVideosReceived,
  rtvChannelSelected,
  showPlayerChooseRtvChannelModal
} from '../actions/player';
import {
  findAvailableRepSlots,
  findRepPartners,
  formatRepDistance,
  getRepBookableSports,
  getRepPartnerWorkspace,
  getRepVideoSearchWindow,
  isRepBookingDateVisible,
  parseRepRequest
} from '../util/rep-assistant';
import {findRepGuide} from '../util/rep-guides';
import {findRepSupportTopic} from '../util/rep-knowledge';
import {getRepToolView, isRepReadOnlyIntent} from '../util/rep-tool-result';
import {findAuthorizedRepContent, openAuthorizedRtvVideo} from '../util/rep-content-open';
import {getRepVoiceProvider, normalizeRepAudioForUpload, selectRepAudioMimeType} from '../util/rep-voice';
import {buildLocalRepMetric, repMetricLocale, repWhisperLanguage} from '../util/rep-metrics';
import RepPartnerQuickActions from './rep-partner-quick-actions';
import {myVideoActiveTab} from '../actions/player';
import {myVideoActiveTab as partnerMyVideoActiveTab} from '../actions/partner';
import {ROLES} from '../constants';
import * as api from '../api';

const exampleKeys = ['REP_EXAMPLE_BOOKING', 'REP_EXAMPLE_TOPUP', 'REP_EXAMPLE_TRANSACTIONS', 'REP_EXAMPLE_HIGHLIGHT', 'REP_EXAMPLE_RTV', 'REP_EXAMPLE_PADEL'];
const repSportLabelKeys = {
  tennis: 'SPORT_TENNIS',
  padel: 'SPORT_PADEL',
  pickleball: 'SPORT_PICKLEBALL'
};

const formatPlannerDateParam = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const translatedToolSubtitle = (subtitle, translate, shouldTranslate) => {
  if (!shouldTranslate) return subtitle;
  return String(subtitle || '').split(' · ').map(part => part.startsWith('REP_TOOL_') ? translate(part) : part).join(' · ');
};

const emptyControlledAction = () => ({status: 'idle', item: null, error: null, downloadStatus: 'idle'});
const emptyCandidateAvailability = () => ({status: 'idle', items: [], errors: 0});

const actionErrorKey = code => ({
  rep_action_expired: 'REP_ACTION_ERROR_EXPIRED',
  rep_action_in_progress: 'REP_ACTION_ERROR_IN_PROGRESS',
  rep_action_idempotency_conflict: 'REP_ACTION_ERROR_CONFLICT',
  ranking_required_for_sport: 'REP_ACTION_ERROR_RANKING_REQUIRED',
  partner_level_range_required: 'REP_ACTION_ERROR_PARTNER_LEVEL',
  slot_not_available_for_open_match: 'REP_ACTION_ERROR_SLOT_UNAVAILABLE',
  open_match_already_exists_for_slot: 'REP_ACTION_ERROR_SLOT_UNAVAILABLE',
  not_enough_available_credit: 'REP_ACTION_ERROR_CREDIT',
  open_matches_not_enabled_for_partner: 'REP_ACTION_ERROR_NOT_ENABLED'
}[code] || 'REP_ACTION_ERROR_GENERIC');

const controlledActionKey = action => action ? `rep-action-${action.id}` : null;

const RepAssistantContent = ({token, profile, partners, favouritePartners, ownedFields, ownedServices, ownedRtvChannels, permanentVideos, partnerVideos, playlists, location, openPlanner, openReplayerTv, openPartnerReplayerTv, openMyVideo, openMyVideoSection, openGuideRoute, openRuntimeRoute, openWalletTopup, openSearchResult}) => {
  const {t, i18n} = useTranslation();
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [voiceMessage, setVoiceMessage] = useState('');
  const [availabilityPartner, setAvailabilityPartner] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState('idle');
  const [availabilityData, setAvailabilityData] = useState({slots: [], fields: []});
  const [candidateAvailability, setCandidateAvailability] = useState(emptyCandidateAvailability);
  const [runtimeKnowledge, setRuntimeKnowledge] = useState({status: 'idle', item: null});
  const [readToolResult, setReadToolResult] = useState({status: 'idle', item: null});
  const [contentOpenStatus, setContentOpenStatus] = useState({status: 'idle', contentId: null});
  const [controlledAction, setControlledAction] = useState(emptyControlledAction);
  const [userPosition, setUserPosition] = useState(null);
  const [positionStatus, setPositionStatus] = useState('idle');
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const preparedActionRequestRef = useRef(null);
  const reportedFeedbackRef = useRef(new Set());
  const trackedMetricRef = useRef(new Set());
  const querySourceRef = useRef('text');
  const queryStartedAtRef = useRef(Date.now());
  const voiceStartedAtRef = useRef(null);
  const availabilityRequestRef = useRef(0);
  const candidateAvailabilityRequestRef = useRef(0);
  const searchablePartners = useMemo(() => (partners || []).map(partner => {
    const favourite = (favouritePartners || []).find(item => item.uid === partner.uid);
    return favourite ? {...partner, ...favourite} : partner;
  }), [favouritePartners, partners]);
  const request = useMemo(() => parseRepRequest(submittedQuery, new Date(), i18n.language), [i18n.language, submittedQuery]);
  const slotSearchIntent = ['booking', 'create-open-match'].includes(request.intent);
  const readOnlyIntent = isRepReadOnlyIntent(request.intent);
  const readToolView = useMemo(() => getRepToolView(readToolResult.item, i18n.language), [i18n.language, readToolResult.item]);
  const isPartner = Boolean(profile && profile.user && profile.user.role === ROLES.partner);
  const partnerWorkspace = useMemo(() => getRepPartnerWorkspace({
    profile,
    fields: ownedFields,
    services: ownedServices,
    channels: ownedRtvChannels,
    videos: permanentVideos,
    partnerVideos,
    playlists
  }), [ownedFields, ownedRtvChannels, ownedServices, partnerVideos, permanentVideos, playlists, profile]);
  const guide = useMemo(() => request.intent === 'guide' ? findRepGuide(submittedQuery, i18n.language) : null, [i18n.language, request.intent, submittedQuery]);
  const supportTopic = useMemo(() => request.intent === 'support' ? findRepSupportTopic(submittedQuery, i18n.language) : null, [i18n.language, request.intent, submittedQuery]);
  const needsRuntimeKnowledge = request.intent === 'unknown'
    || (request.intent === 'guide' && !guide)
    || (request.intent === 'support' && !supportTopic);
  const resultLimit = slotSearchIntent && request.sport ? Math.max(searchablePartners.length, 50) : 5;
  const results = useMemo(() => findRepPartners(searchablePartners, request, resultLimit, userPosition), [request, resultLimit, searchablePartners, userPosition]);
  const suggestedSports = useMemo(() => getRepBookableSports(searchablePartners, request.intent), [request.intent, searchablePartners]);
  const videoSearchWindow = useMemo(() => getRepVideoSearchWindow(), []);
  const availableSlots = useMemo(() => {
    if (!availabilityPartner || availabilityStatus !== 'ready') return [];
    return findAvailableRepSlots(availabilityData.slots, availabilityData.fields, request);
  }, [availabilityData.fields, availabilityData.slots, availabilityPartner, availabilityStatus, request]);
  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceProvider = getRepVoiceProvider(process.env.REACT_APP_HEY_REP_VOICE_PROVIDER);
  const maxRecordingSeconds = Math.max(1, Number(process.env.REACT_APP_HEY_REP_VOICE_MAX_SECONDS) || 15);

  useEffect(() => () => {
    availabilityRequestRef.current += 1;
    candidateAvailabilityRequestRef.current += 1;
    if (recognitionRef.current) recognitionRef.current.abort();
    if (recordingTimerRef.current) window.clearTimeout(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(track => track.stop());
  }, []);

  useEffect(() => {
    const suggestedQuestion = new URLSearchParams(location.search || '').get('ask');
    if (!suggestedQuestion) return;
    setQuery(suggestedQuestion);
    querySourceRef.current = 'text';
    queryStartedAtRef.current = Date.now();
    setSubmittedQuery(suggestedQuestion);
  }, [location.search]);

  const trackClientMetric = useCallback(payload => {
    if (!token) return;
    api.trackRepMetric(payload, token);
  }, [token]);

  const reportFeedback = useCallback((category, errorCode = null) => {
    if (!token || !submittedQuery) return;
    const key = `${category}:${submittedQuery}:${errorCode || ''}`;
    if (reportedFeedbackRef.current.has(key)) return;
    reportedFeedbackRef.current.add(key);
    api.createRepFeedback({
      question: submittedQuery,
      category,
      sourcePath: location.pathname,
      attemptedIntent: request.intent,
      attemptedContext: {concepts: request.concepts || [], errorCode}
    }, token);
  }, [location.pathname, request.concepts, request.intent, submittedQuery, token]);

  const prepareControlledAction = useCallback(async (actionType, parameters, requestKey = null) => {
    if (!token) return null;
    setControlledAction({status: 'preparing', item: null, error: null, downloadStatus: 'idle'});
    const result = await api.prepareRepAction({
      actionType,
      parameters,
      locale: i18n.language,
      sourcePath: location.pathname
    }, token);
    if (result.status === 201 && result.json && result.json.action) {
      if (requestKey) preparedActionRequestRef.current = requestKey;
      setControlledAction({status: 'prepared', item: result.json.action, error: null, downloadStatus: 'idle'});
      return result.json.action;
    }
    const error = result.json && result.json.error ? result.json.error : 'rep_action_prepare_failed';
    setControlledAction({status: 'error', item: null, error, downloadStatus: 'idle'});
    reportFeedback('technical_error', error);
    return null;
  }, [i18n.language, location.pathname, reportFeedback, token]);

  const downloadControlledReport = useCallback(async action => {
    const download = action && action.result && action.result.download;
    if (!download) return false;
    setControlledAction(current => ({...current, downloadStatus: 'downloading'}));
    const result = download.kind === 'partner-players'
      ? await api.partnerReportPlayers(token)
      : await api.reportTransactions(download.parameters || {}, token);
    const succeeded = result.status === 200;
    setControlledAction(current => ({...current, downloadStatus: succeeded ? 'downloaded' : 'download-error'}));
    if (!succeeded) reportFeedback('technical_error', 'rep_report_download_failed');
    return succeeded;
  }, [reportFeedback, token]);

  const confirmControlledAction = useCallback(async () => {
    const action = controlledAction.item;
    if (!action || !token) return;
    setControlledAction(current => ({...current, status: 'confirming', error: null}));
    const result = await api.confirmRepAction(action.id, controlledActionKey(action), token);
    if (result.status === 200 && result.json && result.json.action) {
      const confirmed = result.json.action;
      setControlledAction({status: 'succeeded', item: confirmed, error: null, downloadStatus: 'idle'});
      if (confirmed.result && confirmed.result.download) await downloadControlledReport(confirmed);
      return;
    }
    const error = result.json && result.json.error ? result.json.error : 'rep_action_execution_failed';
    setControlledAction(current => ({...current, status: 'error', error}));
    reportFeedback('technical_error', error);
  }, [controlledAction.item, downloadControlledReport, reportFeedback, token]);

  useEffect(() => {
    if (!submittedQuery || !needsRuntimeKnowledge || !token) {
      setRuntimeKnowledge({status: 'idle', item: null});
      return undefined;
    }
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let active = true;
    setRuntimeKnowledge({status: 'loading', item: null});
    api.getRepKnowledge(submittedQuery, token, controller ? controller.signal : undefined, i18n.language).then(result => {
      if (!active || (result.json && result.json.cancelled)) return;
      if (result.status === 200 && result.json && (result.json.answer || result.json.route)) {
        setRuntimeKnowledge({status: 'ready', item: result.json});
        return;
      }
      if (result.status === 404) {
        setRuntimeKnowledge({status: 'not-found', item: null});
        reportFeedback(request.intent === 'unknown' ? 'unrecognized' : 'unsupported');
        return;
      }
      setRuntimeKnowledge({status: 'error', item: null});
      reportFeedback('technical_error', 'rep_knowledge_load_failed');
    });
    return () => {
      active = false;
      if (controller) controller.abort();
    };
  }, [i18n.language, needsRuntimeKnowledge, reportFeedback, request.intent, submittedQuery, token]);

  useEffect(() => {
    if (!submittedQuery || readOnlyIntent || needsRuntimeKnowledge) return;
    const key = `${submittedQuery}:${request.intent}:${querySourceRef.current}`;
    if (trackedMetricRef.current.has(key)) return;
    trackedMetricRef.current.add(key);
    const noResult = (slotSearchIntent || request.intent === 'video') && results.length === 0;
    trackClientMetric(buildLocalRepMetric({
      outcome: noResult ? 'no_result' : 'success',
      intent: request.intent,
      locale: i18n.language,
      channel: querySourceRef.current,
      durationMs: Date.now() - queryStartedAtRef.current
    }));
  }, [i18n.language, needsRuntimeKnowledge, readOnlyIntent, request.intent, results.length, slotSearchIntent, submittedQuery, trackClientMetric]);

  useEffect(() => {
    if (!submittedQuery || !readOnlyIntent || !token) {
      setReadToolResult({status: 'idle', item: null});
      return undefined;
    }
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let active = true;
    setReadToolResult({status: 'loading', item: null});
    setContentOpenStatus({status: 'idle', contentId: null});
    const contentDateRange = request.contentDateRange;
    api.queryRep({
      query: submittedQuery,
      intent: request.intent,
      locale: i18n.language,
      parameters: {
        sport: request.sport,
        limit: 5,
        dateFrom: contentDateRange ? contentDateRange.start.toISOString() : undefined,
        dateTo: contentDateRange ? contentDateRange.end.toISOString() : undefined
      }
    }, token, controller ? controller.signal : undefined).then(result => {
      if (!active || (result.json && result.json.cancelled)) return;
      if (result.status === 200 && result.json && result.json.readOnly === true) {
        setReadToolResult({status: 'ready', item: result.json});
        return;
      }
      setReadToolResult({status: result.status === 422 ? 'unsupported' : 'error', item: null});
      if (result.status !== 422) reportFeedback('technical_error', 'rep_read_tool_failed');
    });
    return () => {
      active = false;
      if (controller) controller.abort();
    };
  }, [i18n.language, readOnlyIntent, reportFeedback, request.contentDateRange, request.intent, request.sport, submittedQuery, token]);

  useEffect(() => {
    if (submittedQuery && request.intent === 'my-video') openMyVideo(isPartner);
  }, [isPartner, openMyVideo, request.intent, submittedQuery]);

  useEffect(() => {
    if (submittedQuery && request.intent === 'my-video-section') openMyVideoSection(request.myVideoSection, request.sectionTarget, isPartner);
  }, [isPartner, openMyVideoSection, request.intent, request.myVideoSection, request.sectionTarget, submittedQuery]);

  useEffect(() => {
    if (submittedQuery && request.intent === 'wallet-topup' && request.amount >= 5 && request.amount <= 9999) {
      openWalletTopup(request.amount, request.paymentMethod, isPartner);
    }
  }, [isPartner, openWalletTopup, request.amount, request.intent, request.paymentMethod, submittedQuery]);

  useEffect(() => {
    if (!submittedQuery || isPartner || !slotSearchIntent || !request.sport || positionStatus !== 'idle') return;
    if (!navigator.geolocation) {
      setPositionStatus('unsupported');
      return;
    }
    setPositionStatus('loading');
    navigator.geolocation.getCurrentPosition(
      position => {
        setUserPosition({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setPositionStatus('ready');
      },
      () => setPositionStatus('denied'),
      {enableHighAccuracy: false, timeout: 6000, maximumAge: 10 * 60 * 1000}
    );
  }, [isPartner, positionStatus, request.sport, slotSearchIntent, submittedQuery]);

  useEffect(() => {
    if (!submittedQuery || isPartner || !slotSearchIntent || !request.sport) {
      setCandidateAvailability(emptyCandidateAvailability());
      return undefined;
    }

    const requestId = candidateAvailabilityRequestRef.current + 1;
    candidateAvailabilityRequestRef.current = requestId;
    let active = true;
    setCandidateAvailability({status: 'loading', items: [], errors: 0});

    Promise.all(results.map(async partner => {
      if (!isRepBookingDateVisible(partner, request.targetDate)) return {partner, status: 'out-of-range'};
      const [fieldsResult, slotsResult] = await Promise.all([
        api.getPartnerFields(partner.uid, token),
        api.getFieldSlots(partner.uid, token, null, false, request.targetDate, 'DAY')
      ]);
      if (fieldsResult.status !== 200 || slotsResult.status !== 200
        || !Array.isArray(fieldsResult.json) || !Array.isArray(slotsResult.json)) {
        return {partner, status: 'error'};
      }
      const slots = findAvailableRepSlots(slotsResult.json, fieldsResult.json, request);
      return {partner, fields: fieldsResult.json, slots: slotsResult.json, availableSlots: slots, status: 'ready'};
    })).then(entries => {
      if (!active || candidateAvailabilityRequestRef.current !== requestId) return;
      const errors = entries.filter(entry => entry.status === 'error').length;
      const items = entries.filter(entry => entry.status === 'ready' && entry.availableSlots.length > 0);
      if (errors === entries.length && entries.length > 0) reportFeedback('technical_error', 'slot_search_failed');
      setCandidateAvailability({status: errors === entries.length && entries.length > 0 ? 'error' : 'ready', items, errors});
    });

    return () => {
      active = false;
    };
  }, [isPartner, reportFeedback, request, results, slotSearchIntent, submittedQuery, token]);

  useEffect(() => {
    const isTransactionReport = request.intent === 'transaction-report' && request.transactionRange;
    const isPlayerReport = request.intent === 'player-report' && isPartner;
    if (!submittedQuery || (!isTransactionReport && !isPlayerReport)) return;
    const parameters = isPlayerReport ? {reportType: 'players'} : {
      reportType: 'transactions',
      start: formatPlannerDateParam(request.transactionRange.start),
      end: formatPlannerDateParam(request.transactionRange.end)
    };
    const key = `${submittedQuery}:${JSON.stringify(parameters)}`;
    if (preparedActionRequestRef.current === key) return;
    preparedActionRequestRef.current = key;
    prepareControlledAction('report.generate', parameters, key);
  }, [isPartner, prepareControlledAction, request.intent, request.transactionRange, submittedQuery]);

  const submit = event => {
    event.preventDefault();
    if (query.trim()) {
      setAvailabilityPartner(null);
      setAvailabilityStatus('idle');
      setAvailabilityData({slots: [], fields: []});
      setReadToolResult({status: 'idle', item: null});
      setControlledAction(emptyControlledAction());
      preparedActionRequestRef.current = null;
      availabilityRequestRef.current += 1;
      querySourceRef.current = 'text';
      queryStartedAtRef.current = Date.now();
      setSubmittedQuery(query.trim());
    }
  };

  const openPartnerQuickAction = action => {
    if (action.section) {
      openMyVideoSection(action.section, null, true);
      return;
    }
    openRuntimeRoute(action.route);
  };
  const selectSuggestedSport = sport => {
    const nextQuery = `${submittedQuery} ${sport}`.trim();
    setAvailabilityPartner(null);
    setAvailabilityStatus('idle');
    setAvailabilityData({slots: [], fields: []});
    setControlledAction(emptyControlledAction());
    availabilityRequestRef.current += 1;
    querySourceRef.current = 'text';
    queryStartedAtRef.current = Date.now();
    setQuery(nextQuery);
    setSubmittedQuery(nextQuery);
  };
  const acceptTranscript = transcript => {
    const normalized = (transcript || '').trim();
    if (!normalized) {
      setVoiceStatus('error');
      setVoiceMessage(t('REP_VOICE_NOT_HEARD'));
      return;
    }
    querySourceRef.current = 'voice';
    queryStartedAtRef.current = Date.now();
    setQuery(normalized);
    setSubmittedQuery(normalized);
    setVoiceStatus('success');
    setVoiceMessage(t('REP_VOICE_UNDERSTOOD', {text: normalized}));
  };

  const listenWithBrowser = () => {
    if (!SpeechRecognition) {
      setVoiceStatus('unsupported');
      setVoiceMessage(t('REP_VOICE_UNSUPPORTED'));
      return;
    }
    if (voiceStatus === 'listening' && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = ({it: 'it-IT', en: 'en-US', fr: 'fr-FR', de: 'de-DE'}[i18n.language && i18n.language.split('-')[0]]) || 'it-IT';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      voiceStartedAtRef.current = Date.now();
      setVoiceStatus('listening');
      setVoiceMessage(t('REP_VOICE_LISTENING'));
    };
    recognition.onresult = event => {
      const confidence = event.results[0][0].confidence;
      trackClientMetric({
        eventType: 'voice_transcription', channel: 'voice', outcome: 'success',
        locale: repMetricLocale(i18n.language), provider: 'browser', statusCode: 200,
        durationMs: voiceStartedAtRef.current ? Date.now() - voiceStartedAtRef.current : null,
        confidencePct: Number.isFinite(confidence) ? Math.round(confidence * 100) : null
      });
      acceptTranscript(event.results[0][0].transcript);
    };
    recognition.onerror = event => {
      const messages = {
        'audio-capture': t('REP_VOICE_AUDIO_CAPTURE'),
        'aborted': t('REP_VOICE_ABORTED'),
        'network': t('REP_VOICE_NETWORK'),
        'no-speech': t('REP_VOICE_NO_SPEECH'),
        'not-allowed': t('REP_VOICE_NOT_ALLOWED'),
        'service-not-allowed': t('REP_VOICE_SERVICE_NOT_ALLOWED')
      };
      setVoiceStatus('error');
      setVoiceMessage(messages[event.error] || t('REP_VOICE_GENERIC_ERROR', {error: event.error || t('UNKNOWN_ERROR')}));
      trackClientMetric({
        eventType: 'voice_transcription', channel: 'voice', outcome: event.error === 'aborted' ? 'cancelled' : 'error',
        locale: repMetricLocale(i18n.language), provider: 'browser', statusCode: event.error === 'not-allowed' ? 403 : 500,
        errorCode: event.error === 'aborted' ? null : (event.error || 'browser_voice_error'),
        durationMs: voiceStartedAtRef.current ? Date.now() - voiceStartedAtRef.current : null
      });
    };
    recognition.onend = () => {
      setVoiceStatus(currentStatus => currentStatus === 'listening' ? 'idle' : currentStatus);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const releaseMediaStream = () => {
    if (recordingTimerRef.current) window.clearTimeout(recordingTimerRef.current);
    recordingTimerRef.current = null;
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
  };

  const transcribeRecording = async (chunks, mimeType) => {
    if (!chunks.length) {
      releaseMediaStream();
      setVoiceStatus('error');
      setVoiceMessage(t('REP_VOICE_NOT_HEARD'));
      return;
    }
    setVoiceStatus('uploading');
    setVoiceMessage(t('REP_VOICE_PROCESSING'));
    const recordedAudio = new Blob(chunks, {type: mimeType});
    let upload;
    try {
      upload = await normalizeRepAudioForUpload(
        recordedAudio,
        mimeType,
        window.AudioContext || window.webkitAudioContext
      );
    } catch (_error) {
      releaseMediaStream();
      setVoiceStatus('error');
      setVoiceMessage(t('REP_VOICE_SAFARI_ERROR'));
      return;
    }
    setVoiceStatus('transcribing');
    const language = repWhisperLanguage(i18n.language);
    const result = await api.transcribeRepAudio(upload.audio, `hey-rep.${upload.extension}`, token, language);
    releaseMediaStream();
    if (result.status >= 200 && result.status < 300 && result.json && typeof result.json.text === 'string') {
      acceptTranscript(result.json.text);
      return;
    }
    const errorCode = result.json && result.json.error && result.json.error.code;
    if (voiceProvider === 'auto' && SpeechRecognition) {
      setVoiceMessage(t('REP_VOICE_BROWSER_FALLBACK'));
      listenWithBrowser();
      return;
    }
    const messages = {
      AUDIO_TOO_LONG: t('REP_VOICE_TOO_LONG'), AUDIO_EMPTY: t('REP_VOICE_EMPTY'), AUDIO_DECODE_ERROR: t('REP_VOICE_DECODE_ERROR'), AUDIO_NO_SPEECH: t('REP_VOICE_NO_CLEAR_SPEECH'), AUDIO_FORMAT_UNSUPPORTED: t('REP_VOICE_FORMAT_UNSUPPORTED'), TRANSCRIPTION_TIMEOUT: t('REP_VOICE_TRANSCRIPTION_TIMEOUT'), TRANSCRIPTION_ERROR: t('REP_VOICE_TRANSCRIPTION_ERROR'), TOO_MANY_REQUESTS: t('REP_VOICE_TOO_MANY'), SERVICE_BUSY: t('REP_VOICE_BUSY'), SERVICE_SATURATED: t('REP_VOICE_BUSY'), QUEUE_FULL: t('REP_VOICE_QUEUE_FULL')
    };
    setVoiceStatus('error');
    setVoiceMessage(messages[errorCode] || t('REP_VOICE_TRANSCRIPTION_GENERIC'));
  };

  const listenWithWhisper = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      if (voiceProvider === 'auto') {
        listenWithBrowser();
        return;
      }
      setVoiceStatus('unsupported');
      setVoiceMessage(t('REP_VOICE_RECORDING_UNAVAILABLE'));
      return;
    }
    setVoiceStatus('requesting_permission');
    setVoiceMessage(t('REP_VOICE_REQUESTING_PERMISSION'));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      const mimeType = selectRepAudioMimeType(window.MediaRecorder, window.navigator);
      const recorder = mimeType ? new window.MediaRecorder(stream, {mimeType}) : new window.MediaRecorder(stream);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        releaseMediaStream();
        setVoiceStatus('error');
        setVoiceMessage(t('REP_VOICE_MIC_INTERRUPTED'));
      };
      recorder.onstop = () => {
        const recordedMimeType = (audioChunksRef.current[0] && audioChunksRef.current[0].type) || recorder.mimeType || mimeType || 'audio/webm';
        transcribeRecording(audioChunksRef.current, recordedMimeType);
      };
      recorder.start();
      setVoiceStatus('listening');
      setVoiceMessage(t('REP_VOICE_LISTENING_STOP'));
      recordingTimerRef.current = window.setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
      }, maxRecordingSeconds * 1000);
    } catch (error) {
      releaseMediaStream();
      setVoiceStatus('error');
      setVoiceMessage(error && (error.name === 'NotAllowedError' || error.name === 'SecurityError') ? t('REP_VOICE_NOT_ALLOWED') : t('REP_VOICE_MIC_UNAVAILABLE'));
    }
  };

  const listen = () => {
    if (voiceProvider === 'disabled') {
      setVoiceStatus('unsupported');
      setVoiceMessage(t('REP_VOICE_DISABLED'));
      return;
    }
    if (voiceStatus === 'listening' && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }
    if (voiceProvider === 'browser') {
      listenWithBrowser();
      return;
    }
    listenWithWhisper();
  };

  const showCandidateAvailability = item => {
    availabilityRequestRef.current += 1;
    setAvailabilityPartner(item.partner);
    setAvailabilityData({fields: item.fields, slots: item.slots});
    setAvailabilityStatus('ready');
  };

  const openOpenMatchWizard = () => {
    const params = new URLSearchParams();
    const date = formatPlannerDateParam(request.targetDate);
    if (request.sport) params.set('sport', request.sport);
    if (date) params.set('date', date);
    openRuntimeRoute(`/open-matches/create${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const prepareOpenMatchForSlot = slot => {
    const options = request.openMatchOptions || {};
    return prepareControlledAction('open-match.create', {
      slotId: slot.slotId || slot.id,
      matchType: options.matchType || 'friendly',
      gender: options.gender || 'any',
      playersRequired: options.playersRequired || 4,
      levelRangeMode: options.levelRangeMode || 'similar',
      minLevel: options.minLevel,
      maxLevel: options.maxLevel
    });
  };

  const retryPreparedReport = () => {
    if (request.intent === 'player-report') {
      prepareControlledAction('report.generate', {reportType: 'players'});
      return;
    }
    if (!request.transactionRange) return;
    prepareControlledAction('report.generate', {
      reportType: 'transactions',
      start: formatPlannerDateParam(request.transactionRange.start),
      end: formatPlannerDateParam(request.transactionRange.end)
    });
  };

  const renderControlledAction = retry => {
    const action = controlledAction.item;
    const preview = action && action.preview ? action.preview : {};
    if (controlledAction.status === 'idle') return null;
    if (controlledAction.status === 'preparing') {
      return <div className="rep-assistant-skywork__controlled-action is-loading" role="status"><FaClock/><div><strong>{t('REP_ACTION_PREPARING')}</strong><span>{t('REP_ACTION_PREPARING_HELP')}</span></div></div>;
    }
    if (controlledAction.status === 'error') {
      return <div className="rep-assistant-skywork__controlled-action is-error" role="alert"><FaExclamationTriangle/><div><strong>{t('REP_ACTION_NOT_COMPLETED')}</strong><span>{t(actionErrorKey(controlledAction.error))}</span></div>{retry ? <button type="button" onClick={retry}>{t('RETRY')}</button> : null}</div>;
    }
    return <div className={`rep-assistant-skywork__controlled-action is-${controlledAction.status}`} role="status" aria-live="polite">
      {controlledAction.status === 'succeeded' ? <FaCheckCircle/> : <FaRobot/>}
      <div className="rep-assistant-skywork__controlled-action-copy">
        <strong>{t(controlledAction.status === 'succeeded' ? 'REP_ACTION_COMPLETED' : 'REP_ACTION_CONFIRM_TITLE')}</strong>
        {action && action.actionType === 'report.generate' ? <>
          <span>{preview.reportType === 'players' ? t('REP_ACTION_PLAYER_REPORT_PREVIEW') : t('REP_ACTION_TRANSACTION_REPORT_PREVIEW', {start: preview.start, end: preview.end})}</span>
          {controlledAction.downloadStatus === 'downloaded' ? <small>{t('REP_ACTION_DOWNLOAD_STARTED')}</small> : null}
          {controlledAction.downloadStatus === 'download-error' ? <small className="is-error">{t('REP_ACTION_DOWNLOAD_FAILED')}</small> : null}
        </> : null}
        {action && action.actionType === 'open-match.create' ? <>
          <span>{t('REP_ACTION_OPEN_MATCH_PREVIEW', {sport: preview.sport, club: preview.partnerName, field: preview.fieldName})}</span>
          <small>{new Date(preview.startDatetime).toLocaleString(i18n.language, {dateStyle: 'medium', timeStyle: 'short'})} · {t('REP_ACTION_PLAYERS_REQUIRED', {count: preview.playersRequired})}</small>
          <small>{preview.minLevel !== null && preview.minLevel !== undefined ? t('REP_ACTION_LEVEL_RANGE', {min: preview.minLevel, max: preview.maxLevel}) : t('REP_ACTION_ANY_LEVEL')} · {preview.paymentMode === 'online' ? t('REP_ACTION_PARTICIPANT_PRICE', {price: (Number(preview.participantPriceCents || 0) / 100).toFixed(2)}) : t('REP_ACTION_NO_ONLINE_PAYMENT')}</small>
          <small>{t('REP_ACTION_FIELD_WARNING')}</small>
        </> : null}
      </div>
      <div className="rep-assistant-skywork__controlled-action-buttons">
        {controlledAction.status === 'prepared' ? <button type="button" onClick={confirmControlledAction}><FaCheckCircle/> {t('CONFIRM')}</button> : null}
        {controlledAction.status === 'confirming' ? <button type="button" disabled><FaClock/> {t('REP_ACTION_CONFIRMING')}</button> : null}
        {controlledAction.status === 'succeeded' && action && action.result && action.result.route ? <button type="button" onClick={() => openRuntimeRoute(action.result.route)}><FaPlayCircle/> {t('REP_ACTION_OPEN_RESULT')}</button> : null}
        {controlledAction.status === 'succeeded' && action && action.result && action.result.download && controlledAction.downloadStatus === 'download-error' ? <button type="button" onClick={() => downloadControlledReport(action)}><FaDownload/> {t('RETRY')}</button> : null}
      </div>
    </div>;
  };

  return (
    <main className="rep-assistant-skywork container">
      <header>
        <span><FaRobot/> {t('REP_ASSISTANT')}</span>
        <h1>{t('REP_HELLO')}</h1>
        <p>{t(isPartner ? partnerWorkspace.introKey : 'REP_INTRO_PLAYER')}</p>
      </header>
      <section className="rep-assistant-skywork__conversation">
        <form onSubmit={submit}>
          <label htmlFor="rep-query">{t('REP_HOW_CAN_HELP')}</label>
          <div>
            <FaSearch aria-hidden="true"/>
            <input id="rep-query" value={query} onChange={event => setQuery(event.target.value)} placeholder={t(isPartner ? partnerWorkspace.placeholderKey : 'REP_PLACEHOLDER_PLAYER')} autoComplete="off"/>
            <button type="button" className={`rep-assistant-skywork__mic${voiceStatus === 'listening' ? ' is-listening' : ''}`} onClick={listen} disabled={['requesting_permission', 'uploading', 'transcribing'].includes(voiceStatus)} title={t(voiceStatus === 'listening' ? 'REP_STOP_LISTENING' : 'REP_TALK')} aria-label={t(voiceStatus === 'listening' ? 'REP_STOP_LISTENING' : 'REP_START_VOICE')} aria-pressed={voiceStatus === 'listening'}><FaMicrophone/></button>
            <button type="submit">{t('SEARCH')}</button>
          </div>
        </form>
        {voiceMessage ? <div className={`rep-assistant-skywork__voice-status is-${voiceStatus}`} role="status" aria-live="polite">{voiceMessage}</div> : null}
        {isPartner ? <RepPartnerQuickActions workspace={partnerWorkspace} onOpenAction={openPartnerQuickAction}/> : <div className="rep-assistant-skywork__examples">
          {exampleKeys.map(key => { const example = t(key); return <button type="button" key={key} onClick={() => { preparedActionRequestRef.current = null; setControlledAction(emptyControlledAction()); querySourceRef.current = 'text'; queryStartedAtRef.current = Date.now(); setQuery(example); setSubmittedQuery(example); }}>{example}</button>; })}
        </div>}
      </section>

      {submittedQuery && needsRuntimeKnowledge ? (
        <section className="rep-assistant-skywork__answer" aria-live="polite">
          <h2><FaBookOpen/> {t(runtimeKnowledge.status === 'loading' ? 'REP_KNOWLEDGE_LOADING_TITLE' : 'REP_SEARCH_HELP_TITLE')}</h2>
          {runtimeKnowledge.status === 'loading' ? <p>{t('REP_KNOWLEDGE_LOADING')}</p> : null}
          {runtimeKnowledge.status === 'ready' ? <>
            <p>{runtimeKnowledge.item.answer || t('REP_KNOWLEDGE_ROUTE_READY')}</p>
            {runtimeKnowledge.item.route ? <button type="button" className="rep-assistant-skywork__primary" onClick={() => openRuntimeRoute(runtimeKnowledge.item.route)}><FaPlayCircle/> {t('REP_OPEN_SUGGESTED_PAGE')}</button> : null}
          </> : null}
          {runtimeKnowledge.status === 'not-found' ? <p>{t(request.intent === 'guide' ? 'REP_GUIDE_NOT_RECOGNIZED' : 'REP_SEARCH_HELP')}</p> : null}
          {runtimeKnowledge.status === 'error' ? <p>{t('REP_KNOWLEDGE_LOAD_ERROR')}</p> : null}
        </section>
      ) : null}

      {submittedQuery && readOnlyIntent ? (
        <section className="rep-assistant-skywork__answer rep-assistant-skywork__tool" aria-live="polite">
          <h2><FaRobot/> {t(readToolView ? readToolView.titleKey : 'REP_TOOL_LOADING_TITLE')}</h2>
          {readToolResult.status === 'loading' ? <p>{t('REP_TOOL_LOADING')}</p> : null}
          {readToolResult.status === 'error' ? <p>{t('REP_TOOL_LOAD_ERROR')}</p> : null}
          {readToolResult.status === 'unsupported' ? <p>{t('REP_TOOL_NOT_AVAILABLE')}</p> : null}
          {readToolResult.status === 'ready' && readToolView ? <>
            <p>{t(readToolView.helpKey)}</p>
            {contentOpenStatus.status === 'error' ? <p role="alert">{t('REP_TOOL_CONTENT_STALE')}</p> : null}
            {readToolView.stats.length ? <div className="rep-assistant-skywork__tool-stats">
              {readToolView.stats.map(stat => <div key={stat.labelKey}><span>{t(stat.labelKey)}</span><strong>{stat.value}</strong></div>)}
            </div> : null}
            {readToolView.items.length ? <div className="rep-assistant-skywork__results">
              {readToolView.items.map(item => <article key={item.key}>
                <div><strong>{item.title}</strong><small>{translatedToolSubtitle(item.subtitle, t, item.translateSubtitleParts)}</small></div>
                {item.contentResult ? <button type="button" disabled={contentOpenStatus.status === 'loading'} onClick={async () => {
                  setContentOpenStatus({status: 'loading', contentId: item.contentResult.contentId});
                  const video = await openSearchResult(item.contentResult, token);
                  if (video && openAuthorizedRtvVideo(video)) {
                    setContentOpenStatus({status: 'idle', contentId: null});
                    return;
                  }
                  setContentOpenStatus({status: 'error', contentId: item.contentResult.contentId});
                  reportFeedback('technical_error', 'content_no_longer_available');
                }}><FaPlayCircle/> {t(contentOpenStatus.status === 'loading' && contentOpenStatus.contentId === item.contentResult.contentId ? 'REP_TOOL_OPENING_CONTENT' : 'REP_TOOL_OPEN_CONTENT')}</button> : null}
                {!item.contentResult && item.route ? <button type="button" onClick={() => openRuntimeRoute(item.route)}><FaPlayCircle/> {t('REP_TOOL_VIEW_ITEM')}</button> : null}
              </article>)}
            </div> : (!readToolView.stats.length ? <p>{t(readToolView.emptyKey)}</p> : null)}
            {readToolView.route ? <button type="button" className="rep-assistant-skywork__primary" onClick={() => openRuntimeRoute(readToolView.route)}><FaPlayCircle/> {t(readToolView.actionKey)}</button> : null}
          </> : null}
        </section>
      ) : null}

      {submittedQuery && request.intent === 'wallet-topup' && (!request.amount || request.amount < 5 || request.amount > 9999) ? (
        <section className="rep-assistant-skywork__answer">
          <h2><FaWallet/> {t('REP_TOPUP_TITLE')}</h2>
          <p>{t(!request.amount ? 'REP_TOPUP_AMOUNT_HELP' : 'REP_TOPUP_RANGE')}</p>
        </section>
      ) : null}

      {submittedQuery && request.intent === 'transaction-report' ? (
        <section className="rep-assistant-skywork__answer">
          <h2><FaDownload/> {t('REP_TRANSACTIONS_TITLE')}</h2>
          {request.transactionRange ? <>
            <p>{t('REP_TRANSACTIONS_CONFIRM_HELP', {range: t(request.transactionRange.labelKey)})}</p>
            {request.asksRefundStatus ? <p>{t('REP_REFUND_HELP')}</p> : null}
            {renderControlledAction(retryPreparedReport)}
          </> : <p>{t('REP_TRANSACTIONS_PERIOD_HELP')}</p>}
        </section>
      ) : null}

      {submittedQuery && request.intent === 'player-report' ? (
        <section className="rep-assistant-skywork__answer">
          <h2><FaDownload/> {t('REP_PLAYER_REPORT_TITLE')}</h2>
          {isPartner ? <>
            <p>{t('REP_PLAYER_REPORT_CONFIRM_HELP')}</p>
            {renderControlledAction(retryPreparedReport)}
          </> : <p>{t('REP_PLAYER_REPORT_PARTNER_ONLY')}</p>}
        </section>
      ) : null}

      {submittedQuery && request.intent === 'guide' && guide ? (
        <section className="rep-assistant-skywork__answer">
          <h2><FaBookOpen/> {guide.title}</h2>
          <p>{guide.summary}</p>
          <ol className="rep-assistant-skywork__steps">
            {guide.steps.map(step => <li key={step}>{step}</li>)}
          </ol>
          <button type="button" className="rep-assistant-skywork__primary" onClick={() => openGuideRoute(guide.route, isPartner)}><FaPlayCircle/> {guide.actionLabel}</button>
        </section>
      ) : null}

      {submittedQuery && request.intent === 'support' && supportTopic ? (
        <section className="rep-assistant-skywork__answer rep-assistant-skywork__support">
          <h2><FaBookOpen/> {supportTopic.title}</h2>
          <p>{supportTopic.summary}</p>
          <ul className="rep-assistant-skywork__steps">
            {supportTopic.details.map(detail => <li key={detail}>{detail}</li>)}
          </ul>
          <button type="button" className="rep-assistant-skywork__primary" onClick={() => openGuideRoute(supportTopic.route, isPartner)}><FaPlayCircle/> {supportTopic.actionLabel}</button>
        </section>
      ) : null}

      {submittedQuery && request.intent === 'video' ? (
        <section className="rep-assistant-skywork__answer">
          <h2>{t('REP_VIDEO_SEARCH')}</h2><p>{t('REP_VIDEO_SEARCH_WINDOW', {start: videoSearchWindow.start.toLocaleDateString(i18n.language), end: new Date(videoSearchWindow.end.getTime() - 1).toLocaleDateString(i18n.language)})}</p>
          {isPartner ? <button type="button" className="rep-assistant-skywork__primary" onClick={() => openReplayerTv(true)}><FaTv/> {t('REP_OPEN_PARTNER_RTV')}</button> : results.length ? <div className="rep-assistant-skywork__results">
            {results.map(partner => (
              <article key={partner.uid}>
                <div><strong>{partner.businessName || partner.name}</strong><small>{partner.city || partner.address || t('REP_SPORT_CENTER')}</small></div>
                <button type="button" onClick={() => openPartnerReplayerTv(partner)}><FaTv/> {t('REP_OPEN_THEIR_RTV')}</button>
              </article>
            ))}
          </div> : <button type="button" className="rep-assistant-skywork__primary" onClick={openReplayerTv}><FaTv/> {t('REP_OPEN_RTV')}</button>}
        </section>
      ) : null}

      {submittedQuery && slotSearchIntent ? (
        <section className="rep-assistant-skywork__answer">
          {isPartner ? request.intent === 'create-open-match' ? <>
            <h2><FaUsers/> {t('REP_CREATE_OPEN_MATCH_TITLE')}</h2>
            <p>{t('REP_CREATE_OPEN_MATCH_PARTNER_HELP')}</p>
            <button type="button" className="rep-assistant-skywork__primary" onClick={() => openRuntimeRoute('/partner-open-matches/create')}><FaPlayCircle/> {t('REP_OPEN_CONTROLLED_WIZARD')}</button>
          </> : <>
            <h2>{t('REP_PARTNER_BOOKINGS_TITLE')}</h2>
            <p>{t('REP_PARTNER_BOOKINGS_HELP')}</p>
            <button type="button" className="rep-assistant-skywork__primary" onClick={() => openPlanner(null, request.targetDate, false, true)}><FaCalendarAlt/> {t('REP_OPEN_PARTNER_RESOURCES')}</button>
          </> : <>
          <h2>{request.intent === 'create-open-match' ? t('REP_CREATE_OPEN_MATCH_TITLE') : t(results.length ? 'REP_MATCHING_CENTERS' : 'REP_NO_MATCHING_CENTER')}</h2>
          {!request.sport ? <>
            <p>{t(request.intent === 'create-open-match' ? 'REP_OPEN_MATCH_ASK_SPORT' : 'REP_BOOKING_ASK_SPORT')}</p>
            {suggestedSports.length ? <div className="rep-assistant-skywork__sport-options" role="group" aria-label={t('REP_CHOOSE_SPORT')}>
              {suggestedSports.map(sport => <button type="button" key={sport} onClick={() => selectSuggestedSport(sport)}><FaPlayCircle/> {t(repSportLabelKeys[sport] || sport)}</button>)}
            </div> : <div className="rep-assistant-skywork__voice-status is-error" role="status">{t('REP_NO_BOOKABLE_SPORTS')}</div>}
          </> : <>
          {request.intent === 'create-open-match' ? <p>{t('REP_CREATE_OPEN_MATCH_HELP')}</p> : null}
          <p>{t('REP_REQUESTED_SPORT', {sport: t(repSportLabelKeys[request.sport] || request.sport)})}{request.requestedTime ? t('REP_REQUESTED_TIME', {time: request.requestedTime}) : ''}{t('REP_PLANNER_CHECK_HELP')}{positionStatus === 'ready' ? t('REP_NEAREST_FIRST') : ''}</p>
          {request.sport && positionStatus === 'loading' ? <div className="rep-assistant-skywork__voice-status is-uploading" role="status">{t('REP_LOCATION_LOADING')}</div> : null}
          {request.sport && ['denied', 'unsupported'].includes(positionStatus) ? <div className="rep-assistant-skywork__voice-status" role="status">{t('REP_LOCATION_UNAVAILABLE', {sport: request.sport})}</div> : null}
          {candidateAvailability.status === 'loading' ? <div className="rep-assistant-skywork__voice-status is-uploading" role="status">{t('REP_SEARCHING_COMPATIBLE_SLOTS')}</div> : null}
          {candidateAvailability.status === 'error' ? <div className="rep-assistant-skywork__voice-status is-error" role="alert">{t('REP_SLOTS_LOAD_ERROR')}</div> : null}
          {candidateAvailability.status === 'ready' && candidateAvailability.items.length ? <div className="rep-assistant-skywork__results">
            {candidateAvailability.items.map(item => (
              <article key={item.partner.uid}>
                <div>
                  <strong>{item.partner.businessName || item.partner.name}</strong>
                  <small>{[item.partner.city || item.partner.address || t('REP_SPORT_CENTER'), formatRepDistance(item.partner.repDistanceKm), t('REP_AVAILABLE_SLOTS_COUNT', {count: item.availableSlots.length})].filter(Boolean).join(' · ')}</small>
                </div>
                <button type="button" onClick={() => showCandidateAvailability(item)}><FaSearch/> {t('REP_SHOW_AVAILABLE_SLOTS')}</button>
              </article>
            ))}
          </div> : null}
          {candidateAvailability.status === 'ready' && !candidateAvailability.items.length ? <div className="rep-assistant-skywork__voice-status" role="status">{t(request.intent === 'create-open-match' ? 'REP_NO_OPEN_MATCH_SLOTS' : 'REP_NO_BOOKING_SLOTS', {date: request.targetDate.toLocaleDateString(i18n.language), sport: t(repSportLabelKeys[request.sport] || request.sport)})}</div> : null}
          {availabilityPartner && availabilityStatus === 'ready' ? (
            <div className="rep-assistant-skywork__availability" role="status">
              <FaClock aria-hidden="true"/>
              <div><strong>{t(availableSlots.length ? 'REP_AVAILABILITIES_FOUND' : 'REP_NO_EXACT_AVAILABILITY', {count: availableSlots.length})}</strong><span>{availableSlots.length ? t('REP_AVAILABILITY_AT_CENTER', {center: availabilityPartner.businessName || availabilityPartner.name}) : t('REP_NEARBY_TIMES_HELP')}</span></div>
              <button type="button" onClick={() => openPlanner(availabilityPartner, request.targetDate)}><FaCalendarAlt/> {t('REP_GO_TO_PLANNER')}</button>
              {availableSlots.length ? <div className="rep-assistant-skywork__slot-list">
                {availableSlots.slice(0, 5).map(slot => {
                  const field = availabilityData.fields.find(item => item.id === slot.fieldId);
                  const start = new Date(slot.startDatetime);
                  const end = new Date(slot.endDatetime);
                  return <article key={slot.slotId || `${slot.fieldId}-${slot.startDatetime}`}>
                    <div><strong>{field ? field.name : slot.fieldOrServiceName || t('REP_AVAILABLE_FIELD')}</strong><span>{start.toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})}–{end.toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})}{slot.presetPlaces > 1 ? t('REP_AVAILABLE_PLACES', {count: slot.availablePlaces}) : t('REP_FULL_FIELD')}</span></div>
                    <b>{Number(slot.price) > 0 ? t('REP_SLOT_PRICE', {price: Number(slot.price).toFixed(2), perPerson: slot.presetPlaces > 1 ? t('REP_PER_PERSON') : ''}) : t('REP_PRICE_IN_PLANNER')}</b>
                    {request.intent === 'create-open-match' ? <button type="button" onClick={() => prepareOpenMatchForSlot(slot)} disabled={['preparing', 'confirming'].includes(controlledAction.status)}><FaUsers/> {t('REP_PREPARE_OPEN_MATCH')}</button> : null}
                  </article>;
                })}
              </div> : null}
            </div>
          ) : null}
          {request.intent === 'create-open-match' ? renderControlledAction(null) : null}
          {candidateAvailability.status === 'ready' && !candidateAvailability.items.length ? <button type="button" className="rep-assistant-skywork__primary" onClick={request.intent === 'create-open-match' ? openOpenMatchWizard : () => openPlanner(null, request.targetDate)}><FaSearch/> {t(request.intent === 'create-open-match' ? 'REP_CHANGE_OPEN_MATCH_SEARCH' : 'REP_SEARCH_SPORT_CENTERS')}</button> : null}
          </>}
          </>}
        </section>
      ) : null}
      <aside className="rep-assistant-skywork__safety"><strong>{t('REP_YOU_ARE_IN_CONTROL')}</strong><span>{t('REP_SAFETY_HELP')}</span></aside>
    </main>
  );
};

const RepAssistant = props => {
  const {t} = useTranslation();
  const [featureStatus, setFeatureStatus] = useState({loading: true, enabled: false});
  const {history, token} = props;

  useEffect(() => {
    if (!token) {
      setFeatureStatus({loading: false, enabled: false});
      return undefined;
    }
    let active = true;
    const load = () => api.getRepSettings(token).then(response => {
      if (!active) return;
      if (response.status === 200 && typeof response.json.enabled === 'boolean') {
        setFeatureStatus({loading: false, enabled: response.json.enabled});
      } else {
        setFeatureStatus({loading: false, enabled: true});
      }
    });
    const handleChange = event => {
      if (event.detail && typeof event.detail.enabled === 'boolean') {
        setFeatureStatus({loading: false, enabled: event.detail.enabled});
      } else {
        load();
      }
    };
    load();
    const refreshTimer = window.setInterval(load, 30000);
    window.addEventListener('replayer:rep-feature-changed', handleChange);
    return () => {
      active = false;
      window.clearInterval(refreshTimer);
      window.removeEventListener('replayer:rep-feature-changed', handleChange);
    };
  }, [token]);

  if (featureStatus.loading) {
    return <main className="rep-assistant-skywork rep-assistant-skywork--status" aria-busy="true">
      <section><FaRobot aria-hidden="true"/><h1>{t('LOADING')}</h1></section>
    </main>;
  }
  if (!featureStatus.enabled) {
    return <main className="rep-assistant-skywork rep-assistant-skywork--status">
      <section><FaExclamationTriangle aria-hidden="true"/><h1>{t('REP_TEMPORARILY_UNAVAILABLE')}</h1><p>{t('REP_TEMPORARILY_UNAVAILABLE_HELP')}</p><button type="button" onClick={() => history.push('/')}>{t('BACK_TO_HOME')}</button></section>
    </main>;
  }
  return <RepAssistantContent {...props}/>;
};

const mapStateToProps = ({
  login: {token, profile},
  player: {partners, favouritePartners},
  partner: {ownedFields, ownedServices, ownedRtvChannels, permanentVideos, partnerVideos, playlists}
}) => ({token, profile, partners, favouritePartners, ownedFields, ownedServices, ownedRtvChannels, permanentVideos, partnerVideos, playlists});
const mapDispatchToProps = dispatch => ({
  openPlanner: async (partner, targetDate, loadOnly = false, isPartner = false) => {
    if (isPartner) {
      dispatch(push('/partner-fields'));
      return;
    }
    if (!partner) {
      dispatch(push('/player-show-sport-centers?bookable=true'));
      return;
    }
    dispatch(partnerSelected(partner));
    await dispatch(getPartnerFields(partner.uid));
    await dispatch(getPartnerServices(partner.uid));
    dispatch(filterBookingFieldId(null));
    dispatch(filterBookableFields(true));
    await dispatch(getSlots(partner.uid, null, targetDate, 'field', 'DAY'));
    if (loadOnly) return;
    const dateParam = formatPlannerDateParam(targetDate);
    dispatch(push(`/player-calendars${dateParam ? `?date=${encodeURIComponent(dateParam)}` : ''}`));
  },
  openReplayerTv: isPartner => dispatch(push(isPartner ? '/partner-replayer-tv-home' : '/player-replayer-tv-home')),
  openMyVideo: isPartner => dispatch(push(isPartner ? '/partner-video' : '/player-video')),
  openMyVideoSection: (section, folder, isPartner) => {
    dispatch(isPartner ? partnerMyVideoActiveTab(section) : myVideoActiveTab(section));
    const params = new URLSearchParams({section});
    if (folder) params.set('folder', folder);
    dispatch(push(`${isPartner ? '/partner-video' : '/player-video'}?${params.toString()}`));
  },
  openGuideRoute: (route, isPartner) => {
    const partnerRoutes = {
      '/player-video': '/partner-video',
      '/player-replayer-tv-home': '/partner-replayer-tv-home',
      '/player-show-sport-centers': '/partner-profile',
      '/player-calendars': '/partner-fields'
    };
    let targetRoute = route;
    if (isPartner) {
      const [path, query] = route.split('?');
      const partnerPath = partnerRoutes[path] || path;
      targetRoute = `${partnerPath}${query ? `?${query}` : ''}`;
      if (path === '/player-video' && query) {
        const section = new URLSearchParams(query).get('section');
        if (section) dispatch(partnerMyVideoActiveTab(section));
      }
    }
    dispatch(push(targetRoute));
  },
  openRuntimeRoute: route => dispatch(push(route)),
  openSearchResult: async (result, token) => {
    if (!result || !result.partnerId || !result.rtvChannelId || !token) return null;
    const response = await api.getRtvVideos(token, result.partnerId, result.rtvChannelId);
    if (response.status !== 200 || !Array.isArray(response.json)) return null;
    const video = findAuthorizedRepContent(result, response.json);
    if (video) dispatch(rtvVideosReceived(response.json, []));
    return video;
  },
  openWalletTopup: (amount, paymentMethod) => {
    const params = new URLSearchParams({amount: amount.toString(), from: 'rep'});
    if (paymentMethod) params.set('method', paymentMethod);
    dispatch(push(`/user-top-up-account?${params.toString()}`));
  },
  openPartnerReplayerTv: partner => {
    const channels = partner.rtvChannels || [];
    if (!channels.length) {
      dispatch(push('/player-replayer-tv-home'));
      return;
    }
    dispatch(partnerSelected(partner));
    if (channels.length === 1) {
      const channel = channels[0];
      const publicSlug = partner.publicSlug || partner.public_slug || null;
      dispatch(rtvChannelSelected(channel));
      if (publicSlug) {
        dispatch(push(`/club/${publicSlug}?view=tv&channel_id=${channel.id}#club-matches`));
        return;
      }
    } else {
      dispatch(rtvChannelSelected(null));
    }
    dispatch(showPlayerChooseRtvChannelModal());
  }
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RepAssistant));
